// Single source of truth for debug mode (tester tooling gate). When ON:
//  - SELECT opens the battle-setup modal (script builder) instead of running a
//    battle directly,
//  - ESC during a battle aborts it and returns to the desktop.
//
// Default: on in dev (vite dev server), off in a production build. Force either
// way with the `?debug=1` / `?debug=0` query param.

const param = new URLSearchParams(window.location.search).get('debug');
let enabled = param !== null ? param !== '0' && param !== 'false' : import.meta.env.DEV;

export function isDebugMode(): boolean {
  return enabled;
}

export function setDebugMode(value: boolean): void {
  enabled = value;
}

// Whether the pre-fight intro (VS + character type + gong) plays before a
// battle. Toggleable from the debug setup modal so testers can skip it; the
// choice persists across reloads. Default on; outside debug nothing flips it,
// so the intro always plays for players.
const INTRO_STORAGE_KEY = 'versus.debugBattleIntro';

function loadBattleIntro(): boolean {
  try {
    return localStorage.getItem(INTRO_STORAGE_KEY) !== '0';
  } catch {
    // storage unavailable (private mode / quota) - default on, non-fatal
    return true;
  }
}

let battleIntro = loadBattleIntro();

export function isBattleIntroEnabled(): boolean {
  return battleIntro;
}

export function setBattleIntroEnabled(value: boolean): void {
  battleIntro = value;
  try {
    localStorage.setItem(INTRO_STORAGE_KEY, value ? '1' : '0');
  } catch {
    // storage unavailable (private mode / quota) - dev convenience, non-fatal
  }
}

// Night mode: the arena mounts its half-dark variant (no torches, moonlight
// through a wall hole). Toggleable from the debug setup modal, persists across
// reloads. Default off; outside debug nothing flips it.
const NIGHT_STORAGE_KEY = 'versus.debugNightMode';

function loadNightMode(): boolean {
  try {
    return localStorage.getItem(NIGHT_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

let nightMode = loadNightMode();

export function isNightModeEnabled(): boolean {
  return nightMode;
}

export function setNightModeEnabled(value: boolean): void {
  nightMode = value;
  try {
    localStorage.setItem(NIGHT_STORAGE_KEY, value ? '1' : '0');
  } catch {
    // storage unavailable (private mode / quota) - dev convenience, non-fatal
  }
}

// Snow in the night sky (new-year vibe): flakes falling in the wall hole.
// Applies whenever night visuals are on (debug NIGHT toggle or a mid-battle
// wall break). Toggleable from the debug setup modal, persists across
// reloads. Default off; outside debug nothing flips it.
const SNOW_STORAGE_KEY = 'versus.debugNightSnow';

function loadSnowMode(): boolean {
  try {
    return localStorage.getItem(SNOW_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

let snowMode = loadSnowMode();

export function isSnowModeEnabled(): boolean {
  return snowMode;
}

export function setSnowModeEnabled(value: boolean): void {
  snowMode = value;
  try {
    localStorage.setItem(SNOW_STORAGE_KEY, value ? '1' : '0');
  } catch {
    // storage unavailable (private mode / quota) - dev convenience, non-fatal
  }
}
